import { useContext, useEffect, useState } from "react";
import HTTP from "../../services/httpService";
import CustomHeader from "../common/Header";
import { SettingContext } from "../../routes";
import { useLocation, useNavigate } from "react-router-dom";
import { formatDate } from "../../services/util";
import { Chart } from "react-google-charts";
import { Masonry } from "@mui/lab";
import { Card, Menu, Progress, Select, Skeleton, Typography } from "antd";
import HappyIcon from "./happy.png";
import ConfusedIcon from "./confused.png";
import AngryIcon from "./angry.png";
import "./Reviews.css";
import {
    columnChartConfigurationOptions,
    pieChartConfigurationOption,
} from "../../config";

const { Option } = Select;

const icons = [AngryIcon, ConfusedIcon, HappyIcon];

const { Text } = Typography;

const chartOptions = {
    ...columnChartConfigurationOptions,
    title: "Reviews according to dates",
    colors: ["#7e49a1", "#ed5027", "#fed93b", "#52a216"],
};

function Reviews() {
    const staff_id = localStorage.getItem("x-dashboard-staffid");
    const { state } = useLocation();
    const { hotel_id = [] } = state;
    const { selectedDates, loggedIn } = useContext(SettingContext);
    const navigate = useNavigate();
    const [barChartData, setBarChartData] = useState([]);
    const [totalReviews, setTotalReviews] = useState(0);
    const [selectedReviews, setSelectedReviews] = useState(0);
    const [totalAvgRatings, setTotalAvgRatings] = useState(0);
    const [selectedAvgRatings, setSelectedAvgRatings] = useState(0);
    const [starDistributionData, setStarDistributionData] = useState({});
    const [kioskSelectOptions, setKioskSelectOptions] = useState([]);
    const [staffSelectOptions, setstaffSelectOptions] = useState([]);
    const [kioskPieChartFormData, setKioskPieChartFormData] = useState({});
    const [staffPieChartFormData, setStaffPieChartFormData] = useState({});
    const [kioskPieChartData, setKioskPieChartData] = useState([]);
    const [staffPieChartData, setStaffPieChartData] = useState([]);

    function setdata(
        reviews,
        countOfSelectionReviews,
        avgRating,
        countOfSelectionAvgRating,
    ) {
        setTotalReviews(reviews);
        setSelectedReviews(countOfSelectionReviews);
        setTotalAvgRatings(avgRating);
        setSelectedAvgRatings(countOfSelectionAvgRating);
    }

    useEffect(() => {
        async function fatch() {
            const [start_date, end_date] = selectedDates;
            const formData = {
                hotel_ids: [hotel_id],
                start_date: formatDate(start_date),
                end_date: formatDate(end_date),
                staff_id,
                all_selected: hotel_id.length > 0 ? false : true,
            };
            const { data: reviewsData } = await HTTP(
                "/reviews/getReviewsDashboard",
                formData,
            );

            const kioskPieChartFromData = {
                staff_id,
                start_date: formatDate(start_date),
                end_date: formatDate(end_date),
                kiosk_id: -1,
                hotel_ids: [hotel_id],
                all_selected: hotel_id.length > 0 ? false : true,
            };
            const staffPieChartFormData = {
                staff_id,
                start_date: formatDate(start_date),
                end_date: formatDate(end_date),
                review_staff_id: -1,
                hotel_ids: [hotel_id],
                all_selected: hotel_id.length > 0 ? false : true,
            };

            setKioskPieChartFormData(kioskPieChartFromData);
            setStaffPieChartFormData(staffPieChartFormData);
            const pieChartOptionFormData = { staff_id, hotel_ids: [hotel_id] };
            const [
                { data: kioskPieChart },
                { data: staffPieChart },
                { data: kioskOptions },
                { data: staffOptions },
            ] = await Promise.all([
                await HTTP(
                    "/reviews/getKioskPieChartData",
                    kioskPieChartFromData,
                ),
                await HTTP(
                    "/reviews/getStaffPieChartData",
                    staffPieChartFormData,
                ),
                await HTTP("/reviews/getHotelKiosks", pieChartOptionFormData),
                await HTTP("/reviews/getHotelStaffs", pieChartOptionFormData),
            ]);

            kioskOptions.splice(0, 0, { kiosk_id: -1, kiosk: "All" });
            staffOptions.splice(0, 0, { staff_id: -1, full_name: "All" });
            setKioskSelectOptions(kioskOptions);
            setstaffSelectOptions(staffOptions);
            if (Array.isArray(kioskPieChart) && kioskPieChart.length > 0) {
                setKioskPieChartData(kioskPieChart);
            }
            if (Array.isArray(staffPieChart) && staffPieChart.length > 0) {
                setStaffPieChartData(staffPieChart);
            }

            const {
                dates_values: barChartData,
                total_reviews,
                selected_reviews,
                total_avg_ratings,
                selected_avg_ratings,
                distribution,
            } = reviewsData;
            setStarDistributionData(distribution);

            setdata(
                total_reviews,
                selected_reviews,
                total_avg_ratings,
                selected_avg_ratings,
            );

            setBarChartData(barChartData);
        }
        fatch();
    }, []);

    async function handleStaffReviewSelectionChange(e) {
        const formData = {
            ...staffPieChartFormData,
            ...{ review_staff_id: e },
        };
        setStaffPieChartFormData(formData);
        const { data: staffPieChart } = await HTTP(
            "/reviews/getStaffPieChartData",
            formData,
        );

        if (Array.isArray(staffPieChart) && staffPieChart.length > 0) {
            setStaffPieChartData(staffPieChart);
        }
    }

    async function handleKioskReviewSelectionChange(e) {
        const formData = {
            ...kioskPieChartFormData,
            ...{ kiosk_id: e },
        };
        setKioskPieChartFormData(formData);
        const { data: kioskPieChart } = await HTTP(
            "/reviews/getKioskPieChartData",
            formData,
        );
        if (Array.isArray(kioskPieChart) && kioskPieChart.length > 0) {
            setKioskPieChartData(kioskPieChart);
        }
    }

    async function callDataAPI(...rest) {
        const [formData] = rest;
        formData.all_selected = formData.hotel_ids.length > 0 ? false : true;
        const { data: reviewsData } = await HTTP(
            "/reviews/getReviewsDashboard",
            {
                staff_id,
                ...formData,
            },
        );

        const kioskChartFormData = {
            ...kioskPieChartFormData,
            ...{
                hotel_ids: formData.hotel_ids,
                start_date: formData.start_date,
                end_date: formData.end_date,
                all_selected: formData.all_selected,
            },
        };

        const staffChartFormData = {
            ...staffPieChartFormData,
            ...{
                hotel_ids: formData.hotel_ids,
                start_date: formData.start_date,
                end_date: formData.end_date,
                all_selected: formData.all_selected,
            },
        };
        setKioskPieChartFormData(kioskChartFormData);
        setStaffPieChartFormData(staffChartFormData);
        const pieChartOptionFormData = {
            staff_id,
            hotel_ids: formData.hotel_ids,
        };

        const [
            { data: kioskPieChart },
            { data: staffPieChart },
            { data: kioskOptions },
            { data: staffOptions },
        ] = await Promise.all([
            await HTTP("/reviews/getKioskPieChartData", kioskChartFormData),
            await HTTP("/reviews/getStaffPieChartData", staffChartFormData),
            await HTTP("/reviews/getHotelKiosks", pieChartOptionFormData),
            await HTTP("/reviews/getHotelStaffs", pieChartOptionFormData),
        ]);

        kioskOptions.splice(0, 0, { kiosk_id: -1, kiosk: "All" });
        staffOptions.splice(0, 0, { staff_id: -1, full_name: "All" });
        setKioskSelectOptions(kioskOptions);
        setstaffSelectOptions(staffOptions);

        if (Array.isArray(kioskPieChart) && kioskPieChart.length > 0) {
            setKioskPieChartData(kioskPieChart);
        }
        if (Array.isArray(staffPieChart) && staffPieChart.length > 0) {
            setStaffPieChartData(staffPieChart);
        }

        const {
            dates_values: barChartData,
            total_reviews,
            selected_reviews,
            total_avg_ratings,
            selected_avg_ratings,
            distribution,
        } = reviewsData;
        setStarDistributionData(distribution);

        setdata(
            total_reviews,
            selected_reviews,
            total_avg_ratings,
            selected_avg_ratings,
        );

        setBarChartData(barChartData);
    }

    return (
        <Card className="dashboard-container">
            <CustomHeader callDataAPI={callDataAPI} />
            <Masonry columns={1} spacing={1}>
                <div className="reviews-container">
                    <Card key="bar chart" style={{ width: "69%" }}>
                        <Menu mode="horizontal" selectedKeys="all_reviews">
                            <Menu.Item key="all_reviews">
                                <div>{`${selectedReviews} Reviews`}</div>
                                <Text keyboard>
                                    {`All Time: ${totalReviews}`}{" "}
                                </Text>
                            </Menu.Item>
                            <Menu.Item key="avg_reviews" disabled>
                                <div>{`${selectedAvgRatings.toFixed(
                                    2,
                                )} Average Star Rating`}</div>
                                <Text keyboard>
                                    {`All Time Average: ${totalAvgRatings.toFixed(
                                        2,
                                    )}`}
                                </Text>
                            </Menu.Item>
                        </Menu>
                        {barChartData.length > 0 ? (
                            <Chart
                                width="100%"
                                height="400px"
                                chartType="ColumnChart"
                                options={chartOptions}
                                data={barChartData}
                            />
                        ) : (
                            <Skeleton
                                active
                                title={false}
                                paragraph={{ rows: 10, width: 40 }}
                            />
                        )}
                    </Card>
                    <Card
                        className="review-distribution"
                        title="Review Distribution"
                        style={{ width: "29%" }}
                    >
                        {Object.keys(starDistributionData).length > 0 ? (
                            <>
                                {Object.entries(starDistributionData).map(
                                    ([key, value]) => {
                                        const [, star] = key.split("_");
                                        return (
                                            <div key={key}>
                                                <div
                                                    style={{
                                                        display: "flex",
                                                        justifyContent:
                                                            "space-between",
                                                    }}
                                                >
                                                    <span>{`${value.toFixed(
                                                        2,
                                                    )} %`}</span>
                                                    <img
                                                        alt="review"
                                                        src={icons[star - 1]}
                                                        width="25px"
                                                    />
                                                </div>
                                                <Progress
                                                    percent={value}
                                                    showInfo={false}
                                                />
                                            </div>
                                        );
                                    },
                                )}
                            </>
                        ) : (
                            <Skeleton
                                active
                                title={false}
                                paragraph={{ rows: 10, width: 40 }}
                            />
                        )}
                    </Card>
                </div>
                <div className="piechart-container">
                    <Card
                        style={{ width: "49%" }}
                        className="kiosk-pie-chart-card"
                    >
                        {kioskSelectOptions.length > 0 && (
                            <Select
                                className="review-Select"
                                id="kioskReviews"
                                defaultValue={"-1"}
                                showSearch
                                allowClear={false}
                                onChange={handleKioskReviewSelectionChange}
                                loading={
                                    kioskSelectOptions.length === 0
                                        ? true
                                        : false
                                }
                            >
                                {kioskSelectOptions.map((e) => (
                                    <Option key={e.kiosk_id}>{e.kiosk}</Option>
                                ))}
                            </Select>
                        )}
                        {kioskPieChartData.length > 0 ? (
                            <Chart
                                chartType="PieChart"
                                width="100%"
                                height="400px"
                                data={kioskPieChartData}
                                options={{
                                    ...pieChartConfigurationOption,
                                    title: "Kiosk Reviews",
                                }}
                            />
                        ) : (
                            <Skeleton
                                active
                                title={false}
                                paragraph={{ rows: 10, width: 40 }}
                            />
                        )}
                    </Card>
                    <Card
                        style={{ width: "49%" }}
                        className="staff-pie-chart-card"
                    >
                        {staffSelectOptions.length > 0 && (
                            <Select
                                className="review-Select"
                                id="kioskReviews"
                                defaultValue={"-1"}
                                showSearch
                                allowClear={false}
                                onChange={handleStaffReviewSelectionChange}
                                loading={
                                    staffSelectOptions.length === 0
                                        ? true
                                        : false
                                }
                            >
                                {staffSelectOptions.map((e) => (
                                    <Option key={e.staff_id}>
                                        {e.full_name}
                                    </Option>
                                ))}
                            </Select>
                        )}
                        {staffPieChartData.length > 0 ? (
                            <Chart
                                chartType="PieChart"
                                width="100%"
                                height="400px"
                                data={staffPieChartData}
                                options={{
                                    ...pieChartConfigurationOption,
                                    title: "Staff Reviews",
                                }}
                            />
                        ) : (
                            <Skeleton
                                active
                                title={false}
                                paragraph={{ rows: 10, width: 40 }}
                            />
                        )}
                    </Card>
                </div>
            </Masonry>
        </Card>
    );
}

export { Reviews };
